import { useEffect, useState } from "react";
import {
  LuLibrary,
  LuChevronLeft,
  LuChevronRight,
  LuMoreHorizontal,
  LuPenLine,
  LuSearch,
  LuX,
} from "react-icons/lu";
import { OptionProps, ParamInterface } from "../../../interface-and-types";
import {
  vesselFormSchema,
  vesselFormType,
} from "../../../interface-and-types/vessel.schema";
import Preloader from "../../../app-components/preloader/Preloader";
import apiClient from "../../../api-services/ApiClient";
import { FaPlus } from "react-icons/fa6";
import { ToastContainer, toast } from "react-toastify";
import Select from "react-select";

const CustomOption = (props: any) => (
  <div className="custom-option items-center flex py-2 border-b border-gray-100 hover:bg-blue-100">
    <input
      id={`id_${props.id}`}
      name={`id_${props.id}`}
      type="checkbox"
      checked={props.isSelected}
      onChange={() => {
        props.selectOption(props.data);
      }}
      className="mr-2 ml-2 mt-1"
      style={{ height: "14px", width: "14px" }}
    />
    <label
      className="mt-1 text-sm font-normal"
      onClick={() => {
        props.selectOption(props.data);
      }}
    >
      {props.label}
    </label>
  </div>
);

const VesselManagement = () => {
  const [vessels, setVessels] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchInputDebounce, setSearchInputDebounce] = useState<string>("");
  const [vesselID, setVesselID] = useState<string>("");
  const [serverError, setServerError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [vesselCategories, setVeseelCategories] = useState<OptionProps[]>([]);

  const [formData, setFormData] = useState<vesselFormType>({
    name: "",
    description: "",
    imoNumber: "",
    mmsi: "",
    vesselType: "",
    affiliate: "",
    hireDate: "",
    vesselCategoryId: "",
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof vesselFormType, string>>
  >({});

  // effect for get users list
  useEffect(() => {
    const params = {
      page: currentPage,
      SearchString: searchInput,
      OrderBy: "Descending",
      PageSize: pageSize,
    };

    getVessels(params);
  }, [currentPage, searchInput, pageSize]);

  // debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchInput(searchInputDebounce);
      setCurrentPage(1);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInputDebounce]);

  useEffect(() => {
    getVesselCategories();
  }, []);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCategoryChange = (item: any) => {
    const str = JSON.stringify(item);
    setFormData({ ...formData, vesselCategoryId: str });
  };

  const handleUpdateVesselInput = (item: any) => {
    setVesselID(item.id);

    const vesselCategory = {
      label: item?.vesselCategory.name,
      value: item?.vesselCategory.id,
      id: item?.vesselCategory.id,
      name: "vesselCategoryId",
      key: item?.vesselCategory.id,
    };

    setFormData({
      name: item.name,
      description: item.description,
      imoNumber: item.imoNumber,
      mmsi: item.mmsi,
      vesselType: item.vesselType,
      affiliate: item.affiliate,
      hireDate: item.hireDate,
      vesselCategoryId: JSON.stringify(vesselCategory),
    });
  };

  const getVessels = async (params: ParamInterface) => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("Vessels", { params });
      if (res && res.data) {
        setVessels(res.data.data);
        setCurrentPage(res.data.pageIndex);
        setLastPage(res.data.totalPages);
      } else {
        setVessels([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setVessels([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < lastPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleCreateSubmit = async (e: any) => {
    e.preventDefault();
    setErrors({});
    setServerError("");

    const result = vesselFormSchema.safeParse(formData);

    if (result.success) {
      setLoading(true);

      try {
        const formControl = result.data;

        const data: any = {
          name: formControl.name,
          description: formControl.description,
          imoNumber: formControl.imoNumber,
          mmsi: formControl.mmsi,
          vesselType: formControl.vesselType,
          affiliate: formControl.affiliate,
          hireDate: formControl.hireDate,
          vesselCategoryId: JSON.parse(formControl.vesselCategoryId).value,
        };

        await apiClient.post("Vessels", data);

        const params = {
          page: currentPage,
          SearchString: searchInput,
          OrderBy: "Descending",
          PageSize: 10,
        };

        await getVessels(params);

        toast.success("created successfully!", {
          autoClose: 60000,
          theme: "colored", // Options: "light", "dark", "colored"
        });

        setFormData({
          name: "",
          description: "",
          imoNumber: "",
          mmsi: "",
          vesselType: "",
          affiliate: "",
          hireDate: "",
          vesselCategoryId: "",
        });

        setCreateModalOpen(false);
      } catch (error: any) {
        setServerError(JSON.stringify(error));
      } finally {
        setLoading(false);
      }
    } else {
      const formattedErrors: Partial<Record<keyof vesselFormType, string>> = {};

      result.error.errors.forEach((err: any) => {
        if (err.path.length > 0) {
          const key = err.path[0] as keyof vesselFormType;
          formattedErrors[key] = err.message;
        }
      });

      console.log(formattedErrors);

      setErrors(formattedErrors);
      setServerError("Validation error");
    }
  };

  const handleEditSubmit = async (e: any) => {
    e.preventDefault();
    setErrors({});
    setServerError("");

    const id = vesselID;
    const result = vesselFormSchema.safeParse(formData);

    if (result.success) {
      setLoading(true);
      try {
        const formControl = result.data;

        const data: any = {
          name: formControl.name,
          description: formControl.description,
          imoNumber: formControl.imoNumber,
          mmsi: formControl.mmsi,
          vesselType: formControl.vesselType,
          affiliate: formControl.affiliate,
          hireDate: formControl.hireDate,
          vesselCategoryId: JSON.parse(formControl.vesselCategoryId).value,
        };

        await apiClient.put(`Vessels/${id}`, data);
        toast.success("updated successfully!", {
          autoClose: 60000,
          theme: "colored",
        });

        const params = {
          page: currentPage,
          SearchString: searchInput,
          OrderBy: "Descending",
          PageSize: 10,
        };

        await getVessels(params);

        setFormData({
          name: "",
          description: "",
          imoNumber: "",
          mmsi: "",
          vesselType: "",
          affiliate: "",
          hireDate: "",
          vesselCategoryId: "",
        });

        setEditModalOpen(false);
      } catch (error: any) {
        setServerError(JSON.stringify(error));
      } finally {
        setLoading(false);
      }
    } else {
      const formattedErrors: Partial<Record<keyof vesselFormType, string>> = {};

      result.error.errors.forEach((err: any) => {
        if (err.path.length > 0) {
          const key = err.path[0] as keyof vesselFormType;
          formattedErrors[key] = err.message;
        }
      });

      setServerError("Validation error");
      setErrors(formattedErrors);

      console.log(formattedErrors);
    }
  };

  const emptyInput = () => {
    setFormData({
      name: "",
      description: "",
      imoNumber: "",
      mmsi: "",
      vesselType: "",
      affiliate: "",
      hireDate: "",
      vesselCategoryId: "",
    });

    setServerError("");
  };

  const getVesselCategories = async () => {
    try {
      const res: any = await apiClient.get("VesselCategory");

      const options: OptionProps[] = res.data.data.map(
        (i: any, index: number) => ({
          label: i.name,
          value: i.id,
          name: "VesselCategory",
          key: index,
        })
      );

      setVeseelCategories(options);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  return (
    <>
      <section className="flex justify-between items-center bg-gradient-to-r from-secondaryBlue to-primaryBlue shadow-md border-b border-gray-200">
        <div className="xl:container xl:mx-auto md:px-4 py-4 pb-24 flex justify-between items-center">
          <h4 className="lg:text-[20px] text-white nunito font-medium">
            Manage Vessels
          </h4>

          {/*page actions */}
          <div className="flex gap-2">
            <button
              className="flex gap-1 items-center text-sm bg-red-500  hover:bg-red-700 shadow py-2 rounded-md text-white px-3 transition-colors duration-300 ease-in-out transform"
              onClick={() => {
                setCreateModalOpen(true);
                emptyInput();
              }}
            >
              <FaPlus />
              <span>Add Vessel</span>
            </button>
          </div>
        </div>
      </section>

      {/* page content */}
      <section className="pt-5 mx-auto md:px-4 xl:container relative top-[-100px] xl:top-[-90px]">
        <div className="rounded-lg pb-6 bg-white shadow-sm">
          {/* page filter && search */}
          <div className="p-4 2xl:p-6 flex justify-between">
            <div className="relative xl:w-1/3 w-1/2">
              <input
                value={searchInputDebounce}
                onChange={(e) => setSearchInputDebounce(e.target.value)}
                placeholder="Search by firstname or lastname"
                className="p-2 text-xs border border-gray-300 hover:border-blue-400 rounded-lg w-full"
              />
              <LuSearch className="text-lg top-[7px] absolute right-[9px] text-gray-400" />
            </div>

            <div className="flex gap-2">
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                id="entries"
                className="p-2 py-1.5 text-xs border border-gray-300 hover:border-blue-400 rounded-lg"
              >
                <option value="10">Show 10 entries</option>
                <option value="25">Show 25 entries</option>
                <option value="50">Show 50 entries</option>
                <option value="100">Show 100 entries</option>
                <option value="250">Show 250 entries</option>
                <option value="500">Show 500 entries</option>
                <option value="1000">Show 1000 entries</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto mb-5 overflow-y-auto">
            <table className="min-w-full bg-white mb-4">
              <thead>
                <tr className="w-full bg-slate-100 text-slate-600 capitalize text-[12px] leading-normal border-b shadow-sm">
                  <th className="py-3 px-3 text-left font-medium">Name</th>
                  <th className="py-3 px-3 text-left font-medium">Affiliate</th>
                  <th className="py-3 px-3 text-left font-medium">
                    Description
                  </th>
                  <th className="py-3 px-3 text-left font-medium">
                    IMO Number
                  </th>
                  <th className="py-3 px-3 text-left font-medium">MMSI</th>
                  <th className="py-3 px-3 text-left font-medium">
                    Vessel Type
                  </th>
                  <th className="py-3 px-3 text-left font-medium">
                    Created At
                  </th>
                  <th className="py-3 px-3 text-left font-medium">Actions</th>
                </tr>
              </thead>

              {!!vessels?.length && (
                <tbody>
                  {vessels?.map((item: any, i: number) => {
                    return (
                      <tr
                        key={item.id}
                        className={`border-b border-[#F2F1F1] hover:bg-gray-100 font-normal text-xs text-slate-600`}
                      >
                        <td className="py-3 px-3 text-left align-top">
                          {item?.name ?? ""}
                        </td>

                        <td className="py-3 px-3 text-left align-top">
                          {item?.affiliate ?? ""}
                        </td>

                        <td className="py-3 px-3 text-left align-top">
                          {item?.description ?? ""}
                        </td>

                        <td className="py-3 px-3 text-left break-words max-w-[250px] align-top cursor-pointer">
                          {item.imoNumber ?? ""}
                        </td>

                        <td className="py-3 px-3 text-left align-top">
                          {item?.mmsi ?? ""}
                        </td>
                        <td className="py-3 px-3 text-left align-top">
                          {item?.vesselType ?? ""}
                        </td>

                        <td className="py-3 px-3 text-left align-top">
                          {item.createdAt ?? ""}
                        </td>

                        <td className="py-3 px-3 text-center flex gap-3 items-center align-top">
                          <button
                            className={`font-medium flex gap-1 items-center text-red-600`}
                            onClick={() => {
                              handleUpdateVesselInput(item);
                              setEditModalOpen(true);
                            }}
                          >
                            <LuPenLine />
                            <span>Update</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>

            {!vessels?.length && (
              <div className="text-center p-5 text-sm text-gray-500 ">
                <LuLibrary className="text-[50px] w-full" />
                <p> No record found</p>
              </div>
            )}
          </div>

          <div className="flex justify-center gap-3 flex-wrap">
            {/* Previous Button */}
            <button
              onClick={handlePrevious}
              disabled={currentPage === 1}
              className={`bg-[#fff] text-gray-500 text-xs p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] font-normal ${
                currentPage === 1
                  ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                  : ""
              }`}
            >
              <span>Previous</span>
            </button>

            {/* Left Arrow */}
            <button
              disabled={currentPage === 1}
              onClick={handlePrevious}
              className={`bg-[#fff] text-gray-400 p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] text-base font-medium ${
                currentPage === 1
                  ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                  : ""
              }`}
            >
              <LuChevronLeft className="text-xl" />
            </button>

            {/* Page Numbers */}
            {Array.from(
              { length: lastPage > 7 ? 3 : lastPage },
              (_, i) => i + 1
            ).map((page) => (
              <button
                title={`page ${page}`}
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`p-1 px-3 rounded-lg border-2 border-solid border-[#F2F1F1] font-normal text-xs ${
                  page === currentPage
                    ? "bg-red-600 text-white"
                    : "bg-[#fff] text-gray-600"
                }`}
              >
                <span>{page}</span>
              </button>
            ))}

            {lastPage > 7 && (
              <>
                {/* ellipse Arrow */}
                <button
                  disabled
                  className={`bg-[#fff] text-gray-600 p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1]`}
                >
                  <LuMoreHorizontal className="text-xl" />
                </button>

                {/* Page Numbers */}
                {Array.from({ length: 3 }, (_, i) => 2 - i).map((page) => (
                  <button
                    title={`page ${lastPage - page}`}
                    key={lastPage - page}
                    onClick={() => setCurrentPage(lastPage - page)}
                    className={`p-1 px-3 rounded-lg border-2 border-solid border-[#F2F1F1] text-base font-normal text-xs ${
                      lastPage - page === currentPage
                        ? "bg-red-600 text-white"
                        : "bg-[#fff] text-gray-600"
                    }`}
                  >
                    <span>{lastPage - page}</span>
                  </button>
                ))}
              </>
            )}

            {/* Right Arrow */}
            <button
              disabled={currentPage === lastPage}
              onClick={handleNext}
              className={`bg-[#fff] text-gray-400 p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] ${
                currentPage === lastPage
                  ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                  : ""
              }`}
            >
              <LuChevronRight className="text-xl" />
            </button>

            {/* Next Button */}
            <button
              onClick={handleNext}
              disabled={currentPage === lastPage}
              className={`bg-[#fff] text-gray-500 text-xs p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] font-normal ${
                currentPage === lastPage
                  ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                  : ""
              }`}
            >
              <span>Next</span>
            </button>
          </div>
        </div>
      </section>

      {/* create - */}
      {isCreateModalOpen && (
        <section className="fixed inset-0 bg-black bg-opacity-50 flex flex-col justify-center items-center">
          <button
            onClick={() => setCreateModalOpen(false)}
            className="mt-4 text-red-600 hover:underline p-3 rounded-full bg-white mb-4 text-xl border border-gray-500 shadow animate-modalShow"
          >
            <LuX />
          </button>

          <div className="bg-white rounded shadow-md w-full max-w-xl animate-modalShow">
            <form onSubmit={handleCreateSubmit} autoComplete="off">
              <h2 className="text-sm font-medium mb-4 flex-col border-b p-3 px-6 bg-gray-100 rounded-t">
                Add new vessel
              </h2>

              <div className="grid grid-cols-1 gap-3 gap-y-3 sm:grid-cols-2 w-full  px-6 pb-16 max-h-[90vh] overflow-y-scroll mb-4">
                <div className="col-span-1">
                  <label className="block text-gray-700 text-xs" htmlFor="name">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`mt-1 p-2 border border-gray-300 rounded w-full text-sm`}
                  />

                  <span className="text-red-600 text-[10px]">
                    {errors.name}
                  </span>
                </div>

                <div className="col-span-1">
                  <label
                    className="block text-gray-700 text-xs"
                    htmlFor="description"
                  >
                    Description
                  </label>
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className={`mt-1 p-2 border border-gray-300 rounded w-full text-sm`}
                  />

                  <span className="text-red-600 text-[10px]">
                    {errors.description}
                  </span>
                </div>

                <div className="col-span-1">
                  <label
                    className="block text-gray-700 text-xs"
                    htmlFor="imoNumber"
                  >
                    IMO Number
                  </label>
                  <input
                    type="text"
                    name="imoNumber"
                    value={formData.imoNumber}
                    onChange={handleChange}
                    className={`mt-1 p-2 border border-gray-300 rounded w-full text-sm`}
                  />

                  <span className="text-red-600 text-[10px]">
                    {errors.imoNumber}
                  </span>
                </div>

                <div className="col-span-1">
                  <label className="block text-gray-700 text-xs" htmlFor="mmsi">
                    MMSI
                  </label>
                  <input
                    type="text"
                    name="mmsi"
                    value={formData.mmsi}
                    onChange={handleChange}
                    className={`mt-1 p-2 border border-gray-300 rounded w-full text-sm`}
                  />

                  <span className="text-red-600 text-[10px]">
                    {errors.mmsi}
                  </span>
                </div>

                <div className="col-span-1">
                  <label
                    className="block text-gray-700 text-xs"
                    htmlFor="vesselType"
                  >
                    Vessel Type
                  </label>
                  <input
                    type="text"
                    name="vesselType"
                    value={formData.vesselType}
                    onChange={handleChange}
                    className={`mt-1 p-2 border border-gray-300 rounded w-full text-sm`}
                  />

                  <span className="text-red-600 text-[10px]">
                    {errors.vesselType}
                  </span>
                </div>

                <div className="col-span-1">
                  <label
                    className="block text-gray-700 text-xs"
                    htmlFor="affiliate"
                  >
                    Affiliate
                  </label>
                  <input
                    type="text"
                    name="affiliate"
                    value={formData.affiliate}
                    onChange={handleChange}
                    className={`mt-1 p-2 border border-gray-300 rounded w-full text-sm`}
                  />

                  <span className="text-red-600 text-[10px]">
                    {errors.affiliate}
                  </span>
                </div>

                <div className="col-span-1">
                  <label
                    className="block text-gray-700 text-xs"
                    htmlFor="hireDate"
                  >
                    Hire Date
                  </label>
                  <input
                    type="date"
                    name="hireDate"
                    value={formData.hireDate}
                    onChange={handleChange}
                    className={`mt-1 p-2 border border-gray-300 rounded w-full text-sm`}
                  />

                  <span className="text-red-600 text-[10px]">
                    {errors.hireDate}
                  </span>
                </div>

                <div className="col-span-1">
                  <label className="block text-gray-700 text-xs" htmlFor="role">
                    Select Category
                  </label>

                  <Select
                    options={vesselCategories}
                    value={
                      !!formData.vesselCategoryId
                        ? JSON.parse(formData.vesselCategoryId)
                        : {}
                    }
                    onChange={handleCategoryChange}
                    placeholder="Select options..."
                    className="mt-1"
                    components={{
                      Option: CustomOption,
                    }}
                  />

                  <span className="text-red-600 text-[10px]">
                    {errors.vesselCategoryId}
                  </span>
                </div>

                <div className="text-center text-xs col-span-1 sm:col-span-2">
                  <p className="text-red-600">{serverError}</p>
                </div>

                <div className="col-span-1 sm:col-span-2">
                  <button
                    type="submit"
                    className="w-1/2 mx-auto flex font-normal justify-center text-sm bg-red-700 text-white py-2 rounded hover:bg-red-600"
                  >
                    Add Vessel
                  </button>
                </div>
              </div>
            </form>
          </div>
        </section>
      )}

      {/* edit user */}
      {isEditModalOpen && (
        <section className="fixed inset-0 bg-black bg-opacity-50 flex flex-col justify-center items-center">
          <button
            onClick={() => setEditModalOpen(false)}
            className="mt-4 text-red-600 hover:underline p-3 rounded-full bg-white mb-4 text-xl border border-gray-500 shadow animate-modalShow"
          >
            <LuX />
          </button>

          <div className="bg-white rounded shadow-md w-full max-w-xl animate-modalShow">
            <form onSubmit={handleEditSubmit} autoComplete="off">
              <h2 className="text-md font-medium mb-4 flex-col border-b p-3 px-6 bg-gray-100 rounded-t">
                Manage Vessel
              </h2>

              <div className="grid grid-cols-1 gap-3 gap-y-3 sm:grid-cols-2 w-full  px-6 pb-16 max-h-[90vh] overflow-y-scroll mb-4">
                <div className="col-span-1">
                  <label
                    className="block text-gray-700 text-xs"
                    htmlFor="firstName"
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`mt-1 p-2 border border-gray-300 rounded w-full text-sm`}
                  />

                  <span className="text-red-600 text-[10px]">
                    {errors.name}
                  </span>
                </div>

                <div className="col-span-1">
                  <label
                    className="block text-gray-700 text-xs"
                    htmlFor="firstName"
                  >
                    Description
                  </label>
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className={`mt-1 p-2 border border-gray-300 rounded w-full text-sm`}
                  />

                  <span className="text-red-600 text-[10px]">
                    {errors.description}
                  </span>
                </div>

                <div className="col-span-1">
                  <label
                    className="block text-gray-700 text-xs"
                    htmlFor="imoNumber"
                  >
                    IMO Number
                  </label>
                  <input
                    type="text"
                    name="imoNumber"
                    value={formData.imoNumber}
                    onChange={handleChange}
                    className={`mt-1 p-2 border border-gray-300 rounded w-full text-sm`}
                  />

                  <span className="text-red-600 text-[10px]">
                    {errors.imoNumber}
                  </span>
                </div>

                <div className="col-span-1">
                  <label className="block text-gray-700 text-xs" htmlFor="mmsi">
                    MMSI
                  </label>
                  <input
                    type="text"
                    name="mmsi"
                    value={formData.mmsi}
                    onChange={handleChange}
                    className={`mt-1 p-2 border border-gray-300 rounded w-full text-sm`}
                  />

                  <span className="text-red-600 text-[10px]">
                    {errors.mmsi}
                  </span>
                </div>

                <div className="col-span-1">
                  <label
                    className="block text-gray-700 text-xs"
                    htmlFor="vesselType"
                  >
                    Vessel Type
                  </label>
                  <input
                    type="text"
                    name="vesselType"
                    value={formData.vesselType}
                    onChange={handleChange}
                    className={`mt-1 p-2 border border-gray-300 rounded w-full text-sm`}
                  />

                  <span className="text-red-600 text-[10px]">
                    {errors.vesselType}
                  </span>
                </div>

                <div className="col-span-1">
                  <label
                    className="block text-gray-700 text-xs"
                    htmlFor="affiliate"
                  >
                    Affiliate
                  </label>
                  <input
                    type="text"
                    name="affiliate"
                    value={formData.affiliate}
                    onChange={handleChange}
                    className={`mt-1 p-2 border border-gray-300 rounded w-full text-sm`}
                  />

                  <span className="text-red-600 text-[10px]">
                    {errors.affiliate}
                  </span>
                </div>

                <div className="col-span-1">
                  <label
                    className="block text-gray-700 text-xs"
                    htmlFor="hireDate"
                  >
                    Hire Date
                  </label>
                  <input
                    type="date"
                    name="hireDate"
                    value={formData.hireDate}
                    onChange={handleChange}
                    className={`mt-1 p-2 border border-gray-300 rounded w-full text-sm`}
                  />

                  <span className="text-red-600 text-[10px]">
                    {errors.hireDate}
                  </span>
                </div>

                <div className="col-span-1">
                  <label className="block text-gray-700 text-xs" htmlFor="role">
                    Select Category
                  </label>

                  <Select
                    options={vesselCategories}
                    value={
                      !!formData.vesselCategoryId
                        ? JSON.parse(formData.vesselCategoryId)
                        : {}
                    }
                    onChange={handleCategoryChange}
                    placeholder="Select options..."
                    className="mt-1"
                    components={{
                      Option: CustomOption,
                    }}
                  />

                  <span className="text-red-600 text-[10px]">
                    {errors.vesselCategoryId}
                  </span>
                </div>

                <div className="text-center text-xs col-span-1 sm:col-span-2">
                  <p className="text-red-600">{serverError}</p>
                </div>

                <div className="col-span-1 sm:col-span-2">
                  <button
                    type="submit"
                    className="w-1/2 mx-auto flex font-normal justify-center text-sm bg-red-700 text-white py-2 rounded hover:bg-red-600"
                  >
                    Update Vessel
                  </button>
                </div>
              </div>
            </form>
          </div>
        </section>
      )}

      <ToastContainer position="bottom-left" hideProgressBar />
      <Preloader loading={loading} />
    </>
  );
};

export default VesselManagement;
